import React from 'react';
import { Box, Typography, Container, Button, Menu, MenuItem } from '@mui/material';
import PropTypes from 'prop-types';
import {
  AppBar, Divider, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemText, Toolbar,
  useScrollTrigger, Fab, Fade
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { BIOLOGICAL_NAV_ITEMS } from '../../Pages/Biological/modelCatalog';

const drawerWidth = 240;

const EVOLVING_SYSTEMS = {
    Biological: BIOLOGICAL_NAV_ITEMS,
    Robotic: [
        { label: 'Graph Editor', path: '/robotic/markov-chains' },
        { label: 'Mobility Systems', path: '/robotic/mobility-systems' },
        { label: 'Digital Twins', path: '/digital_twins' },
    ],
    Art: [
        { label: 'Audio-Visualizer', path: '/art/audio-visualizer' },
    ],
};

function ScrollTop(props) {
  const { children } = props;
  const trig = useScrollTrigger({ disableHysteresis: true, threshold: 100 });

  return (
    <Fade in={trig}>
      <Box onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} role="presentation" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 99 }}>
        {children}
      </Box>
    </Fade>
  );
}

function DrawerAppBar(props) {
  const navigate = useNavigate();
  const { window } = props;
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleLogout = () => { logout(); navigate('/'); };

  const navLinkSx = {
    color: '#e0e0e0',
    fontSize: '15px',
    textTransform: 'none',
    '&:hover': { color: '#2cbb5d', backgroundColor: 'rgba(44, 187, 93, 0.1)' },
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', bgcolor: '#18181b', height: '100%', color: 'white' }}>
      <Typography variant="h6" sx={{ my: 2, fontFamily: 'monospace', fontWeight: 700 }}>
        Evolv<span style={{ color: '#2cbb5d' }}>e<sup>x</sup></span>
      </Typography>
      <Divider sx={{ borderColor: '#333' }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }} onClick={() => navigate('/')}>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }} onClick={() => navigate('/arcade')}>
            <ListItemText primary="Arcade" />
          </ListItemButton>
        </ListItem>
        {Object.entries(EVOLVING_SYSTEMS).map(([category, items]) => (
          <React.Fragment key={category}>
            <ListItem>
              <ListItemText primary={category} sx={{ color: '#2cbb5d' }} />
            </ListItem>
            {items.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton sx={{ pl: 4 }} onClick={() => navigate(item.path)}>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </React.Fragment>
        ))}
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }} onClick={() => navigate('/docs')}>
            <ListItemText primary="Docs" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }} onClick={() => navigate('/about')}>
            <ListItemText primary="About" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }} onClick={() => navigate('/contact')}>
            <ListItemText primary="Contact" />
          </ListItemButton>
        </ListItem>
        {user ? (
          <>
            <Divider sx={{ borderColor: '#333', my: 1 }} />
            {user.user_type !== 'guest' && (
              <ListItem disablePadding>
                <ListItemButton sx={{ textAlign: 'center' }} onClick={() => navigate('/account')}>
                  <ListItemText primary="Account" sx={{ color: '#2cbb5d' }} />
                </ListItemButton>
              </ListItem>
            )}
            {user.privilege_level === 'admin' && (
              <ListItem disablePadding>
                <ListItemButton sx={{ textAlign: 'center' }} onClick={() => navigate('/admin')}>
                  <ListItemText primary="Admin" sx={{ color: '#2cbb5d' }} />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton sx={{ textAlign: 'center' }} onClick={handleLogout}>
                <ListItemText primary="Logout" sx={{ color: '#ff4d4d' }} />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <ListItem disablePadding>
            <ListItemButton sx={{ textAlign: 'center' }} onClick={() => navigate('/auth/login')}>
              <ListItemText primary="Sign In" sx={{ color: '#2cbb5d' }} />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 0 });

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        component="nav"
        elevation={0}
        sx={{
          backgroundColor: trigger ? 'rgba(24, 24, 27, 0.95)' : 'transparent',
          backdropFilter: trigger ? 'blur(10px)' : 'none',
          borderBottom: trigger ? '1px solid #333' : 'none',
          transition: 'all 0.3s ease',
          py: 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
              <MenuIcon />
            </IconButton>

            <Typography
              variant="h5"
              component="div"
              sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' }, fontFamily: '"Fira Code", monospace', fontWeight: 700, letterSpacing: '-0.5px', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              Evolv<span style={{ color: '#2cbb5d' }}>e<sup>x</sup></span>
            </Typography>

            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, alignItems: 'center' }}>
              <Button sx={navLinkSx} onClick={() => navigate('/')}>Home</Button>
              <Button sx={navLinkSx} endIcon={<ArrowDropDownIcon />} onClick={(e) => setAnchorEl(e.currentTarget)}>
                Evolving Systems
              </Button>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { bgcolor: '#18181b', color: '#fff', border: '1px solid #333' } }}>
                <MenuItem onClick={() => { setAnchorEl(null); navigate('/arcade'); }}>Arcade</MenuItem>
                <Divider sx={{ borderColor: '#333' }} />
                {Object.entries(EVOLVING_SYSTEMS).map(([category, items]) => (
                  <React.Fragment key={category}>
                    <MenuItem disabled sx={{ opacity: '1 !important', color: '#2cbb5d', fontSize: '0.75rem' }}>{category}</MenuItem>
                    {items.map((item) => (
                      <MenuItem key={item.path} onClick={() => { setAnchorEl(null); navigate(item.path); }} sx={{ pl: 4 }}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </React.Fragment>
                ))}
              </Menu>
              <Button sx={navLinkSx} onClick={() => navigate('/docs')}>Docs</Button>
              <Button sx={navLinkSx} onClick={() => navigate('/about')}>About</Button>
              <Button sx={navLinkSx} onClick={() => navigate('/contact')}>Contact</Button>

              {user ? (
                <>
                  <Box sx={{ width: '1px', height: '20px', bgcolor: '#444', mx: 1 }} />
                  <Typography
                    sx={{ color: '#888', fontSize: '14px', mr: 1, cursor: user.user_type !== 'guest' ? 'pointer' : 'default', '&:hover': user.user_type !== 'guest' ? { color: '#2cbb5d' } : {} }}
                    onClick={() => { if (user.user_type !== 'guest') navigate('/account'); }}
                  >
                    Hi, {user.username}
                  </Typography>
                  {user.user_type !== 'guest' && (
                    <Button sx={{ ...navLinkSx, mr: 1 }} onClick={() => navigate('/account')}>
                      Account
                    </Button>
                  )}
                  {user.privilege_level === 'admin' && (
                    <Button sx={{ ...navLinkSx, mr: 1 }} onClick={() => navigate('/admin')}>
                      Admin
                    </Button>
                  )}
                  <Button variant="outlined" sx={{ color: '#ff4d4d', borderColor: '#ff4d4d', fontWeight: 'bold', fontSize: '13px', textTransform: 'none', padding: '4px 12px', '&:hover': { bgcolor: 'rgba(255, 77, 77, 0.1)', borderColor: '#ff4d4d' } }} onClick={handleLogout}>
                    Logout
                  </Button>
                </>
              ) : (
                <Button variant="contained" sx={{ bgcolor: '#2cbb5d', color: '#000', fontWeight: 'bold', fontSize: '14px', textTransform: 'none', ml: 2, '&:hover': { bgcolor: '#249a4d' } }} onClick={() => navigate('/auth/login')}>
                  Sign In
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <nav>
        <Drawer container={container} variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}>
          {drawer}
        </Drawer>
      </nav>

      <ScrollTop {...props}>
        <Fab size="small" aria-label="scroll back to top" sx={{ bgcolor: '#2cbb5d', '&:hover': { bgcolor: '#249a4d' } }}>
          <KeyboardArrowUpIcon sx={{ color: '#fff' }} />
        </Fab>
      </ScrollTop>
    </Box>
  );
}

DrawerAppBar.propTypes = { window: PropTypes.func };

export default DrawerAppBar;
